import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  Grid,
  Tabs,
  Tab,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Edit as EditIcon,
  CalendarMonth as CalendarIcon,
  LocalShipping as ShippingIcon,
  Numbers as NumbersIcon
} from '@mui/icons-material';
import { orderAPI } from '../../services/api';

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

const SHIPPING_METHODS = [
  { value: 'FEDEX_FIRST_OVERNIGHT', label: 'FedEx First Overnight - $200', cost: 200 },
  { value: 'FEDEX_STANDARD_OVERNIGHT', label: 'FedEx Standard Overnight - $100', cost: 100 },
  { value: 'UPS_NEXT_DAY_AIR_EARLY', label: 'UPS Next Day Air Early - $200', cost: 200 },
  { value: 'UPS_NEXT_DAY_AIR', label: 'UPS Next Day Air - $100', cost: 100 }
];

const PRIORITY_OPTIONS = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

const EditOrderDialog = ({ open, onClose, order, onSave }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    orderDate: '',
    orderNumber: '',
    shippingName: '',
    shippingCompany: '',
    shippingAddress1: '',
    shippingAddress2: '',
    shippingCity: '',
    shippingState: '',
    shippingZipCode: '',
    shippingPhone: '',
    shippingMethod: '',
    deliveryDate: '',
    notes: '',
    priority: 'NORMAL',
    reason: ''
  });

  // Shipping cost state
  const [waiveShipping, setWaiveShipping] = useState(false);
  const [waiveReason, setWaiveReason] = useState('');

  // Initialize form when order changes
  useEffect(() => {
    if (order && open) {
      const orderDate = order.orderDate 
        ? new Date(order.orderDate).toISOString().split('T')[0] 
        : '';
      const deliveryDate = order.estimatedDelivery 
        ? new Date(order.estimatedDelivery).toISOString().split('T')[0] 
        : '';

      setFormData({
        orderDate,
        orderNumber: order.orderNumber || '',
        shippingName: order.shippingName || order.shippingAddress?.name || '',
        shippingCompany: order.shippingCompany || order.shippingAddress?.company || '',
        shippingAddress1: order.shippingAddress1 || order.shippingAddress?.address1 || '',
        shippingAddress2: order.shippingAddress2 || order.shippingAddress?.address2 || '',
        shippingCity: order.shippingCity || order.shippingAddress?.city || '',
        shippingState: order.shippingState || order.shippingAddress?.state || '',
        shippingZipCode: order.shippingZipCode || order.shippingAddress?.zipCode || '',
        shippingPhone: order.shippingPhone || order.shippingAddress?.phone || '',
        shippingMethod: order.shippingMethod || '',
        deliveryDate,
        notes: order.notes || '',
        priority: order.priority || 'NORMAL',
        reason: ''
      });
      setWaiveShipping(order.shippingWaived || false);
      setWaiveReason(order.shippingWaivedReason || '');
      setActiveTab(0);
      setError('');
      setSuccess('');
    }
  }, [order, open]);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const hasPicked = order?.items?.some(item => (item.pickedQuantity || 0) > 0);
  const canEditOrderNumber = order?.isLegacyOrder || !order?.orderNumber?.startsWith('ORD-');

  const handleSaveDate = async () => {
    if (!formData.orderDate) {
      setError('Order date is required');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const response = await orderAPI.editOrderDate(order.id, formData.orderDate, formData.reason);
      if (response.success) {
        setSuccess('Order date updated successfully');
        onSave(response.data);
        setTimeout(() => onClose(), 1500);
      } else {
        setError(response.message || 'Failed to update order date');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveOrderNumber = async () => {
    if (!formData.orderNumber?.trim()) {
      setError('Order number is required');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const response = await orderAPI.editOrderNumber(order.id, formData.orderNumber.trim(), formData.reason);
      if (response.success) {
        setSuccess('Order number updated successfully');
        onSave(response.data);
        setTimeout(() => onClose(), 1500);
      } else {
        setError(response.message || 'Failed to update order number');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveShipping = async () => {
    setSubmitting(true);
    setError('');
    
    try {
      // First update shipping details
      const editResponse = await orderAPI.editOrder(order.id, {
        shippingName: formData.shippingName,
        shippingCompany: formData.shippingCompany,
        shippingAddress1: formData.shippingAddress1,
        shippingAddress2: formData.shippingAddress2,
        shippingCity: formData.shippingCity,
        shippingState: formData.shippingState,
        shippingZipCode: formData.shippingZipCode,
        shippingPhone: formData.shippingPhone,
        shippingMethod: formData.shippingMethod,
        deliveryDate: formData.deliveryDate,
        notes: formData.notes,
        priority: formData.priority,
        reason: formData.reason || 'Shipping details updated'
      });

      // Then update shipping cost if waived
      if (waiveShipping) {
        await orderAPI.updateShippingCost(order.id, {
          waiveShipping: true,
          waiveReason: waiveReason
        });
      } else if (formData.shippingMethod) {
        await orderAPI.updateShippingCost(order.id, {
          shippingMethod: formData.shippingMethod
        });
      }

      if (editResponse.success) {
        setSuccess('Order updated successfully');
        onSave(editResponse.data);
        setTimeout(() => onClose(), 1500);
      } else {
        setError(editResponse.message || 'Failed to update order');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return '#f59e0b';
      case 'CONFIRMED': return '#3b82f6';
      case 'PROCESSING': return '#8b5cf6';
      case 'PICKING': return '#6366f1';
      default: return '#6b7280';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={submitting ? undefined : onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderTop: '4px solid #3b82f6' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EditIcon color="primary" />
        Edit Order {order?.orderNumber}
        <Chip 
          label={order?.status} 
          size="small" 
          sx={{ 
            ml: 'auto',
            bgcolor: getStatusColor(order?.status), 
            color: 'white',
            fontWeight: 600 
          }} 
        />
      </DialogTitle>
      
      <DialogContent>
        {hasPicked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Some items have been picked. You can only edit shipping details and order metadata, not order items.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab icon={<CalendarIcon />} label="Date" iconPosition="start" />
          <Tab icon={<NumbersIcon />} label="Order #" iconPosition="start" disabled={!canEditOrderNumber} />
          <Tab icon={<ShippingIcon />} label="Shipping" iconPosition="start" />
        </Tabs>

        {/* Tab 0: Edit Date */}
        {activeTab === 0 && (
          <Box sx={{ py: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Change the order date for reporting or record-keeping purposes.
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Order Date"
                  type="date"
                  fullWidth
                  value={formData.orderDate}
                  onChange={handleChange('orderDate')}
                  InputLabelProps={{ shrink: true }}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Reason for Change"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.reason}
                  onChange={handleChange('reason')}
                  placeholder="Optional: Explain why the date is being changed..."
                  disabled={submitting}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSaveDate}
                disabled={submitting || !formData.orderDate}
                startIcon={submitting ? <CircularProgress size={16} /> : <EditIcon />}
              >
                {submitting ? 'Saving...' : 'Save Date'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Tab 1: Edit Order Number */}
        {activeTab === 1 && (
          <Box sx={{ py: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Order number editing is primarily for legacy orders imported from external systems.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Order Number"
                  fullWidth
                  value={formData.orderNumber}
                  onChange={handleChange('orderNumber')}
                  disabled={submitting}
                  helperText={order?.originalOrderNumber ? `Original: ${order.originalOrderNumber}` : ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Reason for Change"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.reason}
                  onChange={handleChange('reason')}
                  placeholder="Explain why the order number is being changed..."
                  disabled={submitting}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSaveOrderNumber}
                disabled={submitting || !formData.orderNumber?.trim()}
                startIcon={submitting ? <CircularProgress size={16} /> : <EditIcon />}
              >
                {submitting ? 'Saving...' : 'Save Order Number'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Tab 2: Edit Shipping */}
        {activeTab === 2 && (
          <Box sx={{ py: 2 }}>
            <Grid container spacing={2}>
              {/* Shipping Address */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Shipping Address
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Recipient Name"
                  fullWidth
                  value={formData.shippingName}
                  onChange={handleChange('shippingName')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Company"
                  fullWidth
                  value={formData.shippingCompany}
                  onChange={handleChange('shippingCompany')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address Line 1"
                  fullWidth
                  value={formData.shippingAddress1}
                  onChange={handleChange('shippingAddress1')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address Line 2"
                  fullWidth
                  value={formData.shippingAddress2}
                  onChange={handleChange('shippingAddress2')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="City"
                  fullWidth
                  value={formData.shippingCity}
                  onChange={handleChange('shippingCity')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth disabled={submitting}>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={formData.shippingState}
                    label="State"
                    onChange={handleChange('shippingState')}
                  >
                    {US_STATES.map(state => (
                      <MenuItem key={state.value} value={state.value}>
                        {state.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="ZIP Code"
                  fullWidth
                  value={formData.shippingZipCode}
                  onChange={handleChange('shippingZipCode')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  fullWidth
                  value={formData.shippingPhone}
                  onChange={handleChange('shippingPhone')}
                  disabled={submitting}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
                  Shipping Options
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={submitting}>
                  <InputLabel>Shipping Method</InputLabel>
                  <Select
                    value={formData.shippingMethod}
                    label="Shipping Method"
                    onChange={handleChange('shippingMethod')}
                  >
                    {SHIPPING_METHODS.map(method => (
                      <MenuItem key={method.value} value={method.value}>
                        {method.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Requested Delivery Date"
                  type="date"
                  fullWidth
                  value={formData.deliveryDate}
                  onChange={handleChange('deliveryDate')}
                  InputLabelProps={{ shrink: true }}
                  disabled={submitting}
                />
              </Grid>

              {/* Shipping Waiver */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={waiveShipping}
                      onChange={(e) => setWaiveShipping(e.target.checked)}
                      disabled={submitting}
                    />
                  }
                  label="Waive Shipping Fee"
                />
                {waiveShipping && (
                  <TextField
                    label="Waiver Reason *"
                    fullWidth
                    value={waiveReason}
                    onChange={(e) => setWaiveReason(e.target.value)}
                    placeholder="Enter reason for waiving shipping..."
                    disabled={submitting}
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={submitting}>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={handleChange('priority')}
                  >
                    {PRIORITY_OPTIONS.map(p => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Order Notes"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.notes}
                  onChange={handleChange('notes')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Reason for Edit"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.reason}
                  onChange={handleChange('reason')}
                  placeholder="Optional: Explain the changes being made..."
                  disabled={submitting}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSaveShipping}
                disabled={submitting || (waiveShipping && !waiveReason.trim())}
                startIcon={submitting ? <CircularProgress size={16} /> : <ShippingIcon />}
              >
                {submitting ? 'Saving...' : 'Save Shipping Details'}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOrderDialog;

